import React, { createContext, useContext, useState } from "react";
import { useBubbleTutor } from "../hooks/useBubbleTutor";

interface TutorContextType {
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  selectedConversationId: string | null;
  setSelectedConversationId: (id: string | null) => void;
  conversations: any[];
  loading: boolean;
  error: string | null;
  createConversation: () => Promise<any>;
  deleteConversation: (id: string) => Promise<void>;
  fetchConversations: () => Promise<void>;
  currentView: "list" | "conversation";
  setCurrentView: (view: "list" | "conversation") => void;
}

const TutorContext = createContext<TutorContextType | null>(null);

export const TutorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [currentView, setCurrentView] = useState<"list" | "conversation">(
    "list"
  );
  const {
    conversations,
    loading,
    error,
    currentConversation,
    createConversation,
    deleteConversation,
    fetchConversations: fetchConversationsHook,
  } = useBubbleTutor();

  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(currentConversation?.id || null);

  const openChat = () => setIsChatOpen(true);
  const closeChat = () => {
    setIsChatOpen(false);
  };
  const toggleChat = () => {
    setIsChatOpen((prev) => {
      // If we're closing the chat, reset the view and selected conversation
      if (prev) {
        setCurrentView("list");
        setSelectedConversationId(null);
      }
      return !prev;
    });
  };

  return (
    <TutorContext.Provider
      value={{
        isChatOpen,
        openChat,
        closeChat,
        toggleChat,
        selectedConversationId,
        setSelectedConversationId,
        conversations,
        loading,
        error,
        createConversation,
        deleteConversation,
        fetchConversations: fetchConversationsHook,
        currentView,
        setCurrentView,
      }}
    >
      {children}
    </TutorContext.Provider>
  );
};

export const useTutorContext = (): TutorContextType => {
  const context = useContext(TutorContext);
  if (!context) {
    throw new Error("useTutorContext must be used within a TutorProvider");
  }
  return context;
};
